import React from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Box,
  Chip,
  IconButton,
  Breadcrumbs,
  Link,
  Divider,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import {
  Delete,
  ShoppingCart,
  Home,
  Favorite,
  Share,
  Visibility,
  Login,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import axios from '../utils/axiosConfig';

// Wishlist functionality is handled through customerSlice
import {
  fetchCustomerWishlist,
  removeFromWishlist,
  clearCustomerError
} from '../store/customerSlice';

// Cart functionality is handled through local cartSlice
import { addToCart } from '../store/slices/cartSlice';
import { getFullImageUrl } from '../utils/imageUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

const WishlistPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { wishlist, wishlistLoading, error } = useSelector((state) => state.customer);
  
  // State to store product details with real-time stock information
  const [productDetails, setProductDetails] = React.useState({});
  const [loadingProductDetails, setLoadingProductDetails] = React.useState(false);
  
  // Get wishlist items array from the wishlist object
  const wishlistItems = wishlist?.items || [];
  
  // Function to fetch product details for wishlist items
  const fetchProductDetails = async (items) => {
    if (!items || items.length === 0) return;
    
    setLoadingProductDetails(true);
    const details = {};
    
    try {
      // Fetch product details for each wishlist item
      const promises = items.map(async (item) => {
        try {
          const response = await axios.get(`${API_BASE_URL}/products/${item.productId}`);
          if (response.data.success && response.data.product) {
            details[item.productId] = response.data.product;
          }
        } catch (error) {
          console.error(`Error fetching details for product ${item.productId}:`, error);
          // Keep the original item data if fetch fails
          details[item.productId] = item;
        }
      });
      
      await Promise.all(promises);
      setProductDetails(details);
    } catch (error) {
      console.error('Error fetching product details:', error);
    } finally {
      setLoadingProductDetails(false);
    }
  };
  
  // Fetch wishlist on component mount
  React.useEffect(() => {
    if (isAuthenticated && user?.id) {
      dispatch(fetchCustomerWishlist(user.id));
    }
  }, [dispatch, user?.id, isAuthenticated]);
  
  // Fetch product details when wishlist items change
  React.useEffect(() => {
    if (wishlistItems.length > 0) {
      fetchProductDetails(wishlistItems);
    }
  }, [wishlistItems]);
  
  // Clear errors when component unmounts
  React.useEffect(() => {
    return () => {
      dispatch(clearCustomerError());
    };
  }, [dispatch]);

  // Show login required message if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Wishlist</Typography>
        </Breadcrumbs>

        {/* Login Required */}
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}
        >
          <Login sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Login Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Please login to view your wishlist and save your favorite products.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/login', { state: { from: { pathname: '/wishlist' } } })}
            sx={{ px: 4, mr: 2 }}
          >
            Login
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/register')}
            sx={{ px: 4 }}
          >
            Sign Up
          </Button>
        </Paper>
      </Container>
    );
  }

  // Show loading state
  if (wishlistLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  // Show error state
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Wishlist</Typography>
        </Breadcrumbs>

        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        
        <Button
          variant="contained"
          onClick={() => dispatch(fetchCustomerWishlist(user.id))}
        >
          Retry
        </Button>
      </Container>
    );
  }

  const handleRemoveFromWishlist = async (productId, productName) => {
    if (!isAuthenticated) {
      toast.info('Please login to manage your wishlist');
      navigate('/login', { state: { from: { pathname: '/wishlist' } } });
      return;
    }

    try {
      await dispatch(removeFromWishlist({ customerId: user.id, productId })).unwrap();
      toast.info(`${productName} removed from wishlist`);
    } catch (error) {
      console.error('Remove from wishlist error:', error);
      if (error.includes && error.includes('403')) {
        toast.error('Please login to manage your wishlist');
        navigate('/login', { state: { from: { pathname: '/wishlist' } } });
      } else {
        toast.error(error || 'Failed to remove from wishlist');
      }
    }
  };

  const handleAddToCart = async (item) => {
    if (!user?.id) {
      toast.error('Please login to add items to cart');
      navigate('/login', { state: { from: { pathname: '/wishlist' } } });
      return;
    }

    try {
      // Transform wishlist item to product format expected by local cart
      const productForCart = {
        id: item.productId,
        name: item.name,
        price: item.price,
        discountedPrice: item.discountedPrice,
        primaryImageUrl: item.image,
        category: item.category,
        subCategory: item.subCategory,
        sellerId: item.sellerId,
        sellerName: item.sellerName,
        priceUnit: item.priceUnit,
        stockQuantity: item.stockQuantity || 999, // Default if not available
        maximumOrderQuantity: item.maximumOrderQuantity || 999,
      };

      const quantity = 1;
      dispatch(addToCart({ product: productForCart, quantity }));
      toast.success(`${item.name} added to cart!`);
    } catch (error) {
      console.error('Add to cart error:', error);
      toast.error('Failed to add to cart');
    }
  };

  const handleClearWishlist = () => {
    if (!isAuthenticated) {
      toast.info('Please login to manage your wishlist');
      navigate('/login', { state: { from: { pathname: '/wishlist' } } });
      return;
    }

    if (window.confirm('Are you sure you want to clear your entire wishlist?')) {
      // Clear wishlist by removing all items
      wishlistItems.forEach(item => {
        dispatch(removeFromWishlist({ customerId: user.id, productId: item.productId }));
      });
      toast.info('Wishlist cleared');
    }
  };

  const handleShare = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item.name,
        text: `Check out this ${item.name} on MOS Marketplace`,
        url: `${window.location.origin}/products/${item.productId}`,
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(`${window.location.origin}/products/${item.productId}`);
      toast.success('Product link copied to clipboard!');
    }
  };

  if (wishlistItems.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            component={RouterLink}
            to="/"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Wishlist</Typography>
        </Breadcrumbs>

        {/* Empty Wishlist */}
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          }}
        >
          <Favorite sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Your Wishlist is Empty
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Save items you love to your wishlist and shop them later.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/products')}
            sx={{ px: 4 }}
          >
            Start Shopping
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Home sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Typography color="text.primary">Wishlist</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            My Wishlist
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'} saved
          </Typography>
        </Box>
        {wishlistItems.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleClearWishlist}
            startIcon={<Delete />}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Wishlist Items */}
      <Grid container spacing={3}>
        {wishlistItems.map((item, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={item.productId}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                {/* Product Image */}
                <CardMedia
                  component="img"
                  height="200"
                  image={getFullImageUrl(item.image) || '/placeholder-product.jpg'}
                  alt={item.name}
                  sx={{
                    cursor: 'pointer',
                    objectFit: 'cover',
                  }}
                  onClick={() => navigate(`/products/${item.productId}`)}
                />

                <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                  {/* Product Name */}
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main' },
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.3,
                      fontSize: '16px'
                    }}
                    onClick={() => navigate(`/products/${item.productId}`)}
                  >
                    {item.name}
                  </Typography>

                  {/* Category and Origin */}
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    <Chip 
                      label={item.category} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: '10px', height: '20px' }}
                    />
                    {item.origin && (
                      <Chip 
                        label={item.origin} 
                        size="small" 
                        variant="outlined"
                        color="secondary"
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    )}
                    {item.subCategory && (
                      <Chip
                        label={item.subCategory}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ fontSize: '10px', height: '20px' }}
                      />
                    )}
                  </Box>

                  {/* Product Details */}
                  <Box sx={{ mb: 1 }}>
                    {(() => {
                      // Get product details from fetched data or fallback to wishlist item
                      const productDetail = productDetails[item.productId] || item;
                      const specs = productDetail.specifications || {};
                      const stockQuantity = productDetail.stockQuantity;
                      
                      return (
                        <>
                          {/* Color and Finish */}
                          {(specs.color || specs.finish) && (
                            <Box sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                              {specs.color && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Box 
                                    sx={{ 
                                      width: 12, 
                                      height: 12, 
                                      borderRadius: '50%', 
                                      bgcolor: specs.color.toLowerCase(),
                                      border: '1px solid #ddd'
                                    }} 
                                  />
                                  <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    {specs.color}
                                  </Typography>
                                </Box>
                              )}
                              {specs.finish && (
                                <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                  • {specs.finish}
                                </Typography>
                              )}
                            </Box>
                          )}

                          {/* Stock Status */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Box 
                              sx={{ 
                                width: 8, 
                                height: 8, 
                                borderRadius: '50%', 
                                bgcolor: (stockQuantity && stockQuantity > 0) ? 'success.main' : 'error.main'
                              }} 
                            />
                            <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary' }}>
                              {loadingProductDetails ? 'Loading...' : 
                               (stockQuantity && stockQuantity > 0) ? `${stockQuantity} in stock` : 'Out of stock'}
                            </Typography>
                          </Box>

                          {/* Thickness if available */}
                          {specs.thickness && (
                            <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', display: 'block' }}>
                              Thickness: {specs.thickness}
                            </Typography>
                          )}
                        </>
                      );
                    })()}

                    {/* Seller */}
                    {item.sellerName && (
                      <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.secondary', display: 'block' }}>
                        Sold by: {item.sellerName}
                      </Typography>
                    )}
                  </Box>

                  {/* Price */}
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1, fontSize: '16px' }}>
                    ₹{item.discountedPrice || item.price}
                    {item.discountedPrice && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                          ml: 1,
                          fontSize: '12px'
                        }}
                      >
                        ₹{item.price}
                      </Typography>
                    )}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        ml: 0.5,
                        fontSize: '10px'
                      }}
                    >
                      {item.priceUnit || 'per unit'}
                    </Typography>
                  </Typography>

                  {/* Added Date */}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Added {new Date(item.addedAt).toLocaleDateString()}
                  </Typography>
                </CardContent>

                <Divider />

                <CardActions sx={{ p: 2, pt: 1 }}>
                  <Grid container spacing={1}>
                    {/* Add to Cart Button */}
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ShoppingCart />}
                        onClick={() => handleAddToCart(item)}
                        size="small"
                      >
                        Add to Cart
                      </Button>
                    </Grid>
                    
                    {/* Action Buttons */}
                    <Grid item xs={4}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/products/${item.productId}`)}
                        title="View Details"
                      >
                        <Visibility />
                      </IconButton>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        size="small"
                        onClick={() => handleShare(item)}
                        title="Share"
                      >
                        <Share />
                      </IconButton>
                    </Grid>
                    <Grid item xs={4}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFromWishlist(item.productId, item.name)}
                        title="Remove from Wishlist"
                      >
                        <Delete />
                      </IconButton>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Continue Shopping */}
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/products')}
          sx={{ px: 4 }}
        >
          Continue Shopping
        </Button>
      </Box>
    </Container>
  );
};

export default WishlistPage;